import api from '~/utils/api'
import { createGrowthEventId } from '~/utils/growthEvents'
import { readPublicMusicList } from '~/utils/public-music-list.mjs'
import { sectionMap, getSectionById } from '~/services/musicSectionMapConfig'
import { musicService, type MusicRank } from '~/services/musicService'
import type { PlayerTrack } from '~/stores/player'
import type { PlaybackContext } from '~/services/songPlayback'
import { rankingPath, readableRanking } from './ranking-contract.mjs'
import { DAILY_COMPETITION } from './competition.mjs'
import { isLegacyRankingUnsupported } from './discovery-source.mjs'
import type { CompetitionSnapshot } from './competition-types'
import type { AxiosRequestConfig } from 'axios'
import { serverCache } from '~/server/utils/cache'
import { createHomeSsrDeadline } from '~/utils/home-ssr-deadline.mjs'
import { localeMappings, type SupportedLocale } from '~/i18n/config'

export type DiscoverySource = 'creator' | 'catalog' | 'editor_pick'
// B1 fill-source fields; absent on older servers (creator, count visible).
export type ListSong = PlayerTrack & { rank?: number; rank_delta?: number; is_new?: boolean; recently_published?: boolean; source?: DiscoverySource; play_count_visible?: boolean; display_only?: boolean; rank_is_ordinal?: boolean; award_rank?: number; award_eligible?: boolean; champion?: boolean; listContext?: PlaybackContext }
export interface RankingSummary extends CompetitionSnapshot { ranking_key: string; title_key: string; window_end: number; total: number; top_ids: string[]; fill_window?: '24h' | '72h' | '7d'; catalog_filled?: boolean }
export type HomeRanking = MusicRank & Partial<RankingSummary> & { top_songs: ListSong[]; load_error?: boolean; deferred?: boolean; loading?: boolean }
// One selected tab, the global hero and the daily challenge promotion.
// All three share the same SSR deadline; hidden tabs remain deferred.
export const HOME_RANKING_LIMIT = 3
export const CLIENT_RANKING_CONCURRENCY = 3
let clientReads = 0
const clientQueue: (() => void)[] = []
async function limitedRankingRead<T>(read: () => Promise<T>): Promise<T> {
  if (process.server) return read()
  await new Promise<void>(resolve => {
    const start = () => { clientReads++; resolve() }
    if (clientReads < CLIENT_RANKING_CONCURRENCY) start()
    else clientQueue.push(start)
  })
  try { return await read() } finally { clientReads--; clientQueue.shift()?.() }
}
// All base/language charts, in the prototype's order, precede any additional charts.
export const HOME_CHART_ORDER = ['global-trending', 'new-songs', 'english-trending', 'spanish-trending', 'korean-trending', 'japanese-trending', 'chinese-trending', 'french-trending', 'german-trending']
const chartOrder = (path: string) => HOME_CHART_ORDER.includes(path) ? HOME_CHART_ORDER.indexOf(path) : HOME_CHART_ORDER.length
const options = { timeout: 6500, headers: { 'Content-Type': 'application/json' }, musicmintInlineError: true }

export async function rankingSummary(locale: string): Promise<RankingSummary[]> {
  const read = async (config: AxiosRequestConfig = {}) => {
    const response = await api.post('music/public/v1/rankings/summary', { locale }, localizedOptions(locale, config))
    if (response?.code !== 200 || !Array.isArray(response.data?.items)) throw Object.assign(new Error('Rankings unavailable'), { status: response?.code })
    return response.data.items as RankingSummary[]
  }
  // Shared by home and genre navigation. Browser reads remain uncached.
  return process.server
    ? serverCache.get(`ranking-summary-${locale}`, () => createHomeSsrDeadline(2300).run(read), 60, 2400, { staleTtl: 300 })
    : read()
}
function localizedOptions(locale: string, config: AxiosRequestConfig = {}) {
  return { ...options, ...config, headers: { ...options.headers, 'x-language': localeMappings.shortToFull[locale as SupportedLocale] || 'en_US', ...config.headers } }
}
export async function rankingPage(key: string, page: number, size = 20, locale = 'en', config: AxiosRequestConfig = {}) {
  const requestId = createGrowthEventId()
  const response = await limitedRankingRead(() => api.post('music/public/v1/rankings/music', { ranking_key: key, locale, page, size }, localizedOptions(locale, config)))
  if (response?.code !== 200 || !Array.isArray(response.data?.items)) throw Object.assign(new Error('Ranking unavailable'), { status: response?.code })
  return { ...response, data: { ...response.data, list: response.data.items.map((song: ListSong, index: number) => ({ ...song, listContext: { requestId, position: index + 1 } })) } }
}
export function attributeLegacy(response: any) {
  const requestId = createGrowthEventId()
  const list = readPublicMusicList(response).map((song: ListSong, index: number) => ({ ...song, listContext: { requestId, position: index + 1 } }))
  return { ...response, data: { ...(Array.isArray(response.data) ? {} : response.data), list }, total: response.data?.total ?? response.total }
}
export async function homeRankings(locale: string, legacy: () => Promise<unknown>) {
  const read = () => fetchHomeRankings(locale, legacy)
  if (!process.server) return read()
  const result = await serverCache.get(`home-rankings-${locale}`, read, 30, 2400, {
    staleTtl: 120, shouldCache: value => value.data.every(chart => !chart.load_error),
  })
  // Cached public content must not share growth attribution IDs across visitors.
  return { ...result, data: result.data.map(chart => {
    const requestId = createGrowthEventId()
    return { ...chart, top_songs: chart.top_songs.map((song, index) => ({ ...song, listContext: { requestId, position: index + 1 } })) }
  }) }
}
async function fetchHomeRankings(locale: string, legacy: () => Promise<unknown>) {
  const deadline = process.server ? createHomeSsrDeadline(2300) : null
  const read = <T>(fetch: (config: AxiosRequestConfig) => Promise<T>): Promise<T> => deadline ? deadline.run(fetch) : fetch({})
  let summary: RankingSummary[]
  try { summary = await read(() => rankingSummary(locale)) } catch (cause) {
    // Only an old server (404/426) falls back; network/5xx surfaces as the retryable error state.
    if (!isLegacyRankingUnsupported(cause)) throw cause
    return legacyHomeRankings(locale, legacy, read)
  }
  // The server omits small sections from the summary; never re-add them here.
  // An empty summary is a real empty result, not a reason to read legacy endpoints.
  const slots: HomeRanking[] = summary.map(item => ({ ...item, id: item.ranking_key, section_id: item.ranking_key, selected_option: '', name: readableRanking(item.ranking_key), cover_url: '', count: item.total, created: item.window_end, top_songs: [], deferred: true }))
  await Promise.all(initialHomeCharts(slots).map(chart => readHomeChart(chart, locale, read)))
  return { code: 200, data: slots }
}
function initialHomeCharts(charts: HomeRanking[]) {
  const global = charts.find(chart => chart.ranking_key === 'music_global_trending' || chart.id === 'global-trending')
  const firstTab = charts.find(chart => chart.ranking_key !== DAILY_COMPETITION)
  const challenge = charts.find(chart => chart.ranking_key === DAILY_COMPETITION)
  return [...new Set([global, firstTab, challenge].filter((chart): chart is HomeRanking => Boolean(chart)))].slice(0, HOME_RANKING_LIMIT)
}
async function readHomeChart(chart: HomeRanking, locale: string, read: <T>(fetch: (config: AxiosRequestConfig) => Promise<T>) => Promise<T>, legacy?: () => Promise<unknown>) {
  try {
    if (chart.ranking_key) {
      const data = (await read(config => rankingPage(chart.ranking_key!, 1, 6, locale, config))).data
      Object.assign(chart, { top_songs: data.list.slice(0, 6), status: data.status ?? chart.status, challenge: data.challenge, last_awarded: data.last_awarded, settlement_day: data.settlement_day ?? chart.settlement_day })
    } else {
      chart.top_songs = attributeLegacy(await read(config => limitedRankingRead(() => musicService.getMoreChartMusic(chart.section_id, chart.selected_option, { page: 1, size: 6 }, localizedOptions(locale, config))))).data.list.slice(0, 6)
    }
    chart.load_error = false
  } catch { chart.load_error = true }
  if (!chart.ranking_key) {
    if (!chart.top_songs.length && legacy) {
      try {
        const old = readPublicMusicList(await read(() => limitedRankingRead(legacy))) as MusicRank[]
        chart.top_songs = attributeLegacy({ code: 200, data: old.find(item => getSectionById(item.section_id, 'rank', item.selected_option)?.path === chart.id)?.top_songs || [] }).data.list.slice(0, 6)
        if (chart.top_songs.length) chart.load_error = false
      } catch { /* Preserve the original empty/error state when fallback fails. */ }
    }
    chart.count = chart.top_songs.length
  }
  chart.deferred = false
}
// Per-snapshot single-flight: old locale results only mutate their old chart,
// never a newly loaded locale's list. Failures are retryable; empty is success.
const pendingHomeCharts = new WeakMap<HomeRanking, Promise<void>>()
export function loadHomeRanking(chart: HomeRanking, locale: string, legacy?: () => Promise<unknown>): Promise<void> {
  if (process.server || (!chart.deferred && !chart.load_error)) return Promise.resolve()
  const pending = pendingHomeCharts.get(chart)
  if (pending) return pending
  chart.loading = true
  const request = readHomeChart(chart, locale, fetch => fetch({}), legacy).finally(() => {
    chart.loading = false
    pendingHomeCharts.delete(chart)
  })
  pendingHomeCharts.set(chart, request)
  return request
}
// Old servers without the V3 summary endpoint (404/426).
async function legacyHomeRankings(locale: string, legacy: () => Promise<unknown>, read: <T>(fetch: (config: AxiosRequestConfig) => Promise<T>) => Promise<T>) {
  let fallback: Promise<unknown> | undefined
  const old = () => fallback ||= legacy()
  const sections = [...sectionMap].sort((a, b) => chartOrder(a.path) - chartOrder(b.path))
  const slots: HomeRanking[] = sections.map(section => ({ id: section.path, section_id: section.id || section.path, selected_option: section.selected_option || '', name: section.title, cover_url: '', count: 0, created: 0, top_songs: [], deferred: true }))
  // Keep the old server's navigation, but defer hidden dedicated charts too.
  slots.push(...['ops_featured', 'music_creator_new'].map(key => ({ ranking_key: key, title_key: `ranking.${key}`, id: key, section_id: key, selected_option: '', name: readableRanking(key), cover_url: '', count: 0, created: 0, top_songs: [], deferred: true })))
  await Promise.all(initialHomeCharts(slots).map(chart => readHomeChart(chart, locale, read, old)))
  return { code: 200, data: slots }
}
export function homeRankingPath(chart: HomeRanking) {
  return chart.ranking_key ? `/music/${rankingPath(chart.ranking_key)}` : `/music/${getSectionById(chart.section_id, 'rank', chart.selected_option)?.path || 'global-trending'}`
}
export function legacyChartLinks(items: RankingSummary[]) {
  const paths = new Set(items.map(item => rankingPath(item.ranking_key)))
  return sectionMap.filter(section => !paths.has(section.path))
}
